import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Stock, StockRecipe, StockType, FullBOMResponse, FullBOMEntry } from "../types";
import {
  getStocks,
  getStockRecipes,
  getStockRecipesByParent,
  getFullBOM,
  createStockRecipe,
  updateStockRecipe,
  deleteStockRecipe,
  consumeForProduction,
} from "../services/api";
import PageLoader from "../components/PageLoader";
import AlertModal from "../components/AlertModal";
import ConfirmModal from "../components/ConfirmModal";
import { usePermissions } from "../context/PermissionContext";
import { formatNumber } from "../utils/formatters";
import "./ProductTree.css";

/**
 * Ürün Ağacı (BOM).
 *
 * Sol: üretilen ürünler (mamül / yarı mamül), reçetesi olanlar işaretli.
 * Sağ: seçilen ürünün ağacı — doğrudan malzemeler düzenlenebilir, alt
 * seviyeler alt ürünün kendi reçetesinden gelir (oraya tıklayınca o ürüne
 * geçilir). "Üretim yap" reçeteye göre ihtiyacı önceden hesaplayıp eksik
 * malzemeyi göndermeden gösterir; sunucu zaten eksikte reddeder, kullanıcı
 * bunu deneme-yanılma ile değil baştan görmeli.
 */

const TYPE_LABEL: Record<StockType, string> = {
  [StockType.Hammadde]: "Hammadde",
  [StockType.YariMamul]: "Yarı mamül",
  [StockType.Mamul]: "Mamül",
};
const TYPE_CLASS: Record<StockType, string> = {
  [StockType.Hammadde]: "raw",
  [StockType.YariMamul]: "semi",
  [StockType.Mamul]: "fin",
};
const typeOf = (s?: Stock | null): StockType => s?.stockType ?? StockType.Hammadde;

/** Ekranın çizdiği ağaç düğümü. Sunucu yanıtı (`billOfMaterials`) buna çevrilir. */
interface TreeNode {
  stock: Stock;
  recipeId?: string;          // seviye ≥1: bu satırın StockRecipe kimliği (düzenle/sil için)
  requiredQuantity: number;
  wastagePercentage?: number;
  children: TreeNode[];
}
const toTree = (r: FullBOMResponse): TreeNode => {
  const walk = (e: FullBOMEntry): TreeNode => ({
    stock: e.childStock,
    recipeId: e.id,
    requiredQuantity: e.requiredQuantity,
    wastagePercentage: e.wastagePercentage,
    children: (e.children || []).map(walk),
  });
  const kids = (r.billOfMaterials || []).slice().sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
  return { stock: r.stock, requiredQuantity: 1, children: kids.map(walk) };
};

const I = {
  plus: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>,
  search: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>,
  factory: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21V9l6 4V9l6 4V5h6v16z" /><path d="M8 17h2M13 17h2" /></svg>,
  edit: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>,
  trash: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" /></svg>,
  chev: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 6 6 6-6 6" /></svg>,
  close: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>,
  tree: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="5" rx="1" /><rect x="14" y="16" width="7" height="5" rx="1" /><rect x="14" y="9" width="7" height="5" rx="1" /><path d="M6.5 8v10.5H14M6.5 11.5H14" /></svg>,
  warn: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" /><path d="M12 9v4M12 17h.01" /></svg>,
  arrowIn: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>,
};

type ListFilter = "all" | "with" | "without";

interface RecipeForm {
  parentStockId: string;
  childStockId: string;
  requiredQuantity: number | "";
  wastagePercentage: number | "";
  description: string;
  sortOrder: number | "";
}
interface ProductionForm {
  stockId: string;
  quantity: number | "";
  referenceNo: string;
  movedBy: string;
  relatedOrderId: string;
}

const emptyRecipe = (parentStockId = ""): RecipeForm => ({
  parentStockId, childStockId: "", requiredQuantity: 1, wastagePercentage: 0, description: "", sortOrder: 1,
});

const ProductTree: React.FC = () => {
  const { has } = usePermissions();
  const canCreate = has("product-tree:create");
  const canUpdate = has("product-tree:update");
  const canDelete = has("product-tree:delete");

  const [stocks, setStocks] = useState<Stock[]>([]);
  const [recipes, setRecipes] = useState<StockRecipe[]>([]);
  const [booting, setBooting] = useState(true);
  const [busy, setBusy] = useState(false);

  const [listType, setListType] = useState<StockType.Mamul | StockType.YariMamul>(StockType.Mamul);
  const [listFilter, setListFilter] = useState<ListFilter>("all");
  const [search, setSearch] = useState("");

  const [selected, setSelected] = useState<Stock | null>(null);
  const [bom, setBom] = useState<TreeNode | null>(null);
  const [direct, setDirect] = useState<StockRecipe[]>([]);
  const [treeLoading, setTreeLoading] = useState(false);

  const [recipeModal, setRecipeModal] = useState<{ editing: StockRecipe | null; form: RecipeForm } | null>(null);
  const [prodModal, setProdModal] = useState<ProductionForm | null>(null);

  const [alert, setAlert] = useState<{ isOpen: boolean; message: string; type: "info" | "success" | "warning" | "error" }>({ isOpen: false, message: "", type: "info" });
  const [confirm, setConfirm] = useState<{ isOpen: boolean; message: string; onConfirm: () => void }>({ isOpen: false, message: "", onConfirm: () => {} });
  const say = (message: string, type: "success" | "warning" | "error" = "success") => setAlert({ isOpen: true, message, type });

  // ---- Veri ----
  const stockById = useMemo(() => new Map(stocks.map((s) => [s.id, s])), [stocks]);
  const byType = useMemo(() => {
    const m: Record<number, Stock[]> = { 1: [], 2: [], 3: [] };
    stocks.forEach((s) => { if (s.isActive !== false) m[typeOf(s)].push(s); });
    return m;
  }, [stocks]);
  const recipeCountByParent = useMemo(() => {
    const m = new Map<string, number>();
    recipes.forEach((r) => { if (r.isActive !== false) m.set(r.parentStockId, (m.get(r.parentStockId) || 0) + 1); });
    return m;
  }, [recipes]);

  const loadAll = useCallback(async () => {
    try {
      const [s, r] = await Promise.all([getStocks(), getStockRecipes()]);
      setStocks(s.data || []);
      setRecipes(r.data || []);
    } catch {
      say("Veriler yüklenemedi.", "error");
    } finally {
      setBooting(false);
    }
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  const openProduct = useCallback(async (stock: Stock) => {
    setSelected(stock);
    // Ağaçtan bir yarı mamüle geçilince sol liste de o tipe döner; seçim listede görünür kalsın
    const t = typeOf(stock);
    if (t === StockType.Mamul || t === StockType.YariMamul) setListType(t);
    setTreeLoading(true);
    try {
      const [b, d] = await Promise.all([getFullBOM(stock.id), getStockRecipesByParent(stock.id)]);
      setBom(b.data ? toTree(b.data) : null);
      setDirect((d.data || []).filter((r: StockRecipe) => r.isActive !== false).sort((a: StockRecipe, b2: StockRecipe) => (a.sortOrder || 0) - (b2.sortOrder || 0)));
    } catch {
      setBom(null); setDirect([]);
    } finally {
      setTreeLoading(false);
    }
  }, []);

  // Seçili ürünün listesi başka tipe geçince seçim de o tipe uyacak şekilde temizlenir
  const list = useMemo(() => {
    const q = search.trim().toLowerCase();
    return byType[listType]
      .filter((s) => !q || s.productName.toLowerCase().includes(q) || s.productCode.toLowerCase().includes(q))
      .filter((s) => {
        const n = recipeCountByParent.get(s.id) || 0;
        return listFilter === "all" ? true : listFilter === "with" ? n > 0 : n === 0;
      })
      .sort((a, b) => a.productName.localeCompare(b.productName, "tr"));
  }, [byType, listType, search, listFilter, recipeCountByParent]);

  const refresh = async () => {
    await loadAll();
    if (selected) {
      const fresh = stockById.get(selected.id) || selected;
      await openProduct(fresh);
    }
  };

  // ---- Reçete ----
  const childOptions = (parentId: string): Stock[] => {
    const p = stockById.get(parentId);
    if (!p) return [];
    const pool = typeOf(p) === StockType.Mamul ? [...byType[2], ...byType[1]] : typeOf(p) === StockType.YariMamul ? byType[1] : [];
    return pool.filter((s) => s.id !== parentId);
  };

  const saveRecipe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!recipeModal) return;
    const f = recipeModal.form;
    if (!f.parentStockId || !f.childStockId) return say("Ana ürün ve malzeme seçilmeli.", "warning");
    if (f.parentStockId === f.childStockId) return say("Ana ürün ve malzeme aynı olamaz.", "warning");
    if (!f.requiredQuantity || Number(f.requiredQuantity) <= 0) return say("Miktar sıfırdan büyük olmalı.", "warning");
    const payload = {
      parentStockId: f.parentStockId,
      childStockId: f.childStockId,
      requiredQuantity: Number(f.requiredQuantity),
      wastagePercentage: Number(f.wastagePercentage || 0),
      description: f.description.trim(),
      sortOrder: Number(f.sortOrder || 1),
    };
    try {
      setBusy(true);
      if (recipeModal.editing) {
        await updateStockRecipe(recipeModal.editing.id, { id: recipeModal.editing.id, ...payload, isActive: true });
        say("Reçete güncellendi.");
      } else {
        await createStockRecipe(payload);
        say("Malzeme reçeteye eklendi.");
      }
      setRecipeModal(null);
      // Ana ürün seçili olandan farklıysa ona geç ki kullanıcı eklediğini görsün
      const parent = stockById.get(f.parentStockId);
      if (parent && parent.id !== selected?.id) setListType(typeOf(parent) === StockType.YariMamul ? StockType.YariMamul : StockType.Mamul);
      await loadAll();
      if (parent) await openProduct(parent);
    } catch {
      say("Reçete kaydedilemedi.", "error");
    } finally {
      setBusy(false);
    }
  };

  const removeRecipe = (r: StockRecipe) => {
    const child = stockById.get(r.childStockId)?.productName || "malzeme";
    const parent = stockById.get(r.parentStockId)?.productName || "ürün";
    setConfirm({
      isOpen: true,
      message: `"${parent}" reçetesinden "${child}" çıkarılsın mı?`,
      onConfirm: async () => {
        setConfirm((c) => ({ ...c, isOpen: false }));
        try {
          setBusy(true);
          await deleteStockRecipe(r.id);
          say("Malzeme reçeteden çıkarıldı.");
          await refresh();
        } catch {
          say("Silinemedi.", "error");
        } finally {
          setBusy(false);
        }
      },
    });
  };

  // ---- Üretim ----
  const productionNeeds = useMemo(() => {
    if (!prodModal) return [];
    const qty = Number(prodModal.quantity) || 0;
    return direct.map((r) => {
      const s = stockById.get(r.childStockId);
      const need = r.requiredQuantity * qty * (1 + (r.wastagePercentage || 0) / 100);
      const have = s?.currentStock ?? 0;
      return { r, s, need, have, short: Math.max(0, need - have) };
    });
  }, [prodModal, direct, stockById]);
  const shortages = productionNeeds.filter((n) => n.short > 0);

  const runProduction = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!prodModal) return;
    const qty = Number(prodModal.quantity) || 0;
    if (qty <= 0) return say("Üretim miktarı sıfırdan büyük olmalı.", "warning");
    if (direct.length === 0) return say("Bu ürünün reçetesi yok; önce malzeme ekleyin.", "warning");
    try {
      setBusy(true);
      await consumeForProduction({
        stockId: prodModal.stockId,
        quantity: qty,
        referenceNo: prodModal.referenceNo.trim() || undefined,
        movedBy: prodModal.movedBy.trim() || undefined,
        relatedOrderId: prodModal.relatedOrderId.trim() || undefined,
      });
      say(`${formatNumber(qty)} ${selected?.unit || "birim"} üretildi; reçetedeki malzemeler stoktan düşüldü.`);
      setProdModal(null);
      await refresh();
    } catch (err: any) {
      say(err?.response?.data?.message || "Üretim yapılamadı — yeterli stok olmayabilir.", "error");
    } finally {
      setBusy(false);
    }
  };

  // ---- Ağaç ----
  const Node: React.FC<{ item: TreeNode; level: number }> = ({ item, level }) => {
    const [open, setOpen] = useState(true);
    const kids = item.children || [];
    const t = typeOf(item.stock);
    // Yalnız doğrudan malzemeler buradan düzenlenir; alt seviyeler alt ürünün reçetesidir.
    const recipe = level === 1
      ? direct.find((r) => r.id === item.recipeId) || direct.find((r) => r.childStockId === item.stock.id)
      : undefined;
    const low = item.stock.minimumStock != null && item.stock.currentStock <= item.stock.minimumStock;
    const canDrill = level > 0 && t !== StockType.Hammadde;
    return (
      <li className={`pt-node l${Math.min(level, 4)}`}>
        <div className="pt-node-row">
          <button
            type="button"
            className={`pt-node-toggle ${kids.length ? "" : "empty"} ${open ? "open" : ""}`}
            onClick={() => kids.length && setOpen((o) => !o)}
            aria-label={open ? "Daralt" : "Genişlet"}
          >
            {kids.length > 0 && I.chev}
          </button>
          <span className={`pt-type ${TYPE_CLASS[t]}`}>{TYPE_LABEL[t]}</span>
          <div className="pt-node-main">
            <span className="code">{item.stock.productCode}</span>
            {canDrill ? (
              <button type="button" className="name link" onClick={() => openProduct(item.stock)} title="Bu ürünün reçetesine geç">
                {item.stock.productName}{I.arrowIn}
              </button>
            ) : (
              <span className="name">{item.stock.productName}</span>
            )}
          </div>
          {level > 0 && (
            <span className="pt-node-qty">
              <b>{formatNumber(item.requiredQuantity)}</b> {item.stock.unit}
              {!!item.wastagePercentage && item.wastagePercentage > 0 && <em>+%{formatNumber(item.wastagePercentage)} fire</em>}
            </span>
          )}
          <span className={`pt-node-stock ${low ? "low" : ""}`} title="Mevcut stok">
            {low && I.warn}{formatNumber(item.stock.currentStock)} {item.stock.unit}
          </span>
          {recipe && (canUpdate || canDelete) && (
            <span className="pt-node-actions">
              {canUpdate && <button type="button" className="pt-ib" title="Düzenle" onClick={() => setRecipeModal({ editing: recipe, form: { parentStockId: recipe.parentStockId, childStockId: recipe.childStockId, requiredQuantity: recipe.requiredQuantity, wastagePercentage: recipe.wastagePercentage || 0, description: recipe.description || "", sortOrder: recipe.sortOrder || 1 } })}>{I.edit}</button>}
              {canDelete && <button type="button" className="pt-ib danger" title="Reçeteden çıkar" onClick={() => removeRecipe(recipe)}>{I.trash}</button>}
            </span>
          )}
        </div>
        {open && kids.length > 0 && <ul className="pt-children">{kids.map((c, i) => <Node key={c.stock.id + i} item={c} level={level + 1} />)}</ul>}
      </li>
    );
  };

  if (booting) return <PageLoader message="Ürün ağacı yükleniyor…" />;

  const selectedCount = selected ? recipeCountByParent.get(selected.id) || 0 : 0;
  const withRecipe = (t: StockType) => byType[t].filter((s) => (recipeCountByParent.get(s.id) || 0) > 0).length;

  return (
    <div className="pt-page">
      <div className="pt-head">
        <div>
          <span className="pt-kicker">Stok yönetimi</span>
          <h1>Ürün Ağacı</h1>
          <p>Mamül ve yarı mamüllerin hangi malzemelerden, ne oranda üretildiği. Üretim yapıldığında malzemeler bu reçeteye göre stoktan düşer.</p>
        </div>
        {canCreate && (
          <div className="pt-head-actions">
            <button type="button" className="pt-btn primary" onClick={() => setRecipeModal({ editing: null, form: emptyRecipe(selected?.id) })}>{I.plus}Reçeteye malzeme ekle</button>
          </div>
        )}
      </div>

      <div className="pt-kpis">
        <div className="pt-kpi fin"><span className="l">Mamül</span><span className="v">{byType[3].length}</span><span className="s">{withRecipe(StockType.Mamul)} tanesinin reçetesi var</span></div>
        <div className="pt-kpi semi"><span className="l">Yarı mamül</span><span className="v">{byType[2].length}</span><span className="s">{withRecipe(StockType.YariMamul)} tanesinin reçetesi var</span></div>
        <div className="pt-kpi raw"><span className="l">Hammadde</span><span className="v">{byType[1].length}</span><span className="s">reçetelerde kullanılan malzeme</span></div>
        <div className="pt-kpi"><span className="l">Reçete satırı</span><span className="v">{recipes.filter((r) => r.isActive !== false).length}</span><span className="s">{recipeCountByParent.size} üründe</span></div>
      </div>

      <div className="pt-work">
        {/* ===== Sol: ürün listesi ===== */}
        <aside className="pt-list">
          <div className="pt-list-head">
            <div className="pt-seg">
              <button type="button" className={listType === StockType.Mamul ? "on" : ""} onClick={() => setListType(StockType.Mamul)}>Mamül <span>{byType[3].length}</span></button>
              <button type="button" className={listType === StockType.YariMamul ? "on" : ""} onClick={() => setListType(StockType.YariMamul)}>Yarı mamül <span>{byType[2].length}</span></button>
            </div>
            <label className="pt-search">{I.search}<input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Kod veya ad ara…" /></label>
            <div className="pt-chips">
              {(["all", "with", "without"] as ListFilter[]).map((f) => (
                <button key={f} type="button" className={listFilter === f ? "on" : ""} onClick={() => setListFilter(f)}>
                  {f === "all" ? "Tümü" : f === "with" ? "Reçeteli" : "Reçetesiz"}
                </button>
              ))}
            </div>
          </div>
          <ul className="pt-items">
            {list.length === 0 && (
              <li className="pt-empty">
                {byType[listType].length === 0
                  ? <><b>Bu tipte ürün yok</b>Stok Yönetimi'nden {TYPE_LABEL[listType].toLowerCase()} tipinde stok kartı açın.</>
                  : "Eşleşen ürün yok."}
              </li>
            )}
            {list.map((s) => {
              const n = recipeCountByParent.get(s.id) || 0;
              return (
                <li key={s.id}>
                  <button type="button" className={`pt-item ${selected?.id === s.id ? "on" : ""}`} onClick={() => openProduct(s)}>
                    <span className="pt-item-top"><span className="code">{s.productCode}</span><span className="stock">{formatNumber(s.currentStock)} {s.unit}</span></span>
                    <span className="pt-item-name">{s.productName}</span>
                    <span className={`pt-item-tag ${n ? "" : "none"}`}>{n ? `${n} malzeme` : "reçete yok"}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </aside>

        {/* ===== Sağ: ağaç ===== */}
        <section className="pt-tree">
          {!selected ? (
            <div className="pt-blank">
              <span className="pt-blank-icon">{I.tree}</span>
              <b>Bir ürün seçin</b>
              Soldaki listeden bir mamül ya da yarı mamül seçince reçetesi burada ağaç olarak açılır.
            </div>
          ) : (
            <>
              <div className="pt-tree-head">
                <div className="pt-tree-title">
                  <span className={`pt-type ${TYPE_CLASS[typeOf(selected)]}`}>{TYPE_LABEL[typeOf(selected)]}</span>
                  <h2>{selected.productName}</h2>
                  <span className="pt-tree-meta">{selected.productCode} · Mevcut <b>{formatNumber(selected.currentStock)} {selected.unit}</b> · {selectedCount} doğrudan malzeme</span>
                </div>
                <div className="pt-tree-actions">
                  {canCreate && <button type="button" className="pt-btn" onClick={() => setRecipeModal({ editing: null, form: emptyRecipe(selected.id) })}>{I.plus}Malzeme ekle</button>}
                  <button type="button" className="pt-btn primary" disabled={direct.length === 0} title={direct.length === 0 ? "Önce reçete tanımlayın" : ""} onClick={() => setProdModal({ stockId: selected.id, quantity: 1, referenceNo: `URE-${new Date().getFullYear()}-${String(Date.now()).slice(-6)}`, movedBy: "", relatedOrderId: "" })}>{I.factory}Üretim yap</button>
                </div>
              </div>

              {treeLoading ? (
                <div className="pt-loading"><span className="pt-spin" />Reçete yükleniyor…</div>
              ) : !bom || (bom.children || []).length === 0 ? (
                <div className="pt-blank small">
                  <b>Reçete tanımlı değil</b>
                  Bu ürünün hangi malzemelerden üretildiği henüz girilmemiş. {canCreate ? "“Malzeme ekle” ile başlayın." : ""}
                </div>
              ) : (
                <>
                  <div className="pt-legend">
                    <span><i className="pt-type fin" />Mamül</span><span><i className="pt-type semi" />Yarı mamül</span><span><i className="pt-type raw" />Hammadde</span>
                    <span className="hint">Miktarlar 1 {selected.unit} ürün içindir. Yarı mamüle tıklayarak kendi reçetesine geçebilirsiniz.</span>
                  </div>
                  <ul className="pt-root"><Node item={bom} level={0} /></ul>
                </>
              )}
            </>
          )}
        </section>
      </div>

      {/* ===== Reçete modalı ===== */}
      {recipeModal && (
        <div className="pt-overlay" onClick={() => setRecipeModal(null)}>
          <form className="pt-modal" onClick={(e) => e.stopPropagation()} onSubmit={saveRecipe}>
            <div className="pt-modal-head">
              <div><span className="pt-kicker">Reçete</span><h3>{recipeModal.editing ? "Malzemeyi düzenle" : "Reçeteye malzeme ekle"}</h3></div>
              <button type="button" className="pt-ib" onClick={() => setRecipeModal(null)} aria-label="Kapat">{I.close}</button>
            </div>
            <div className="pt-modal-body">
              <label className="pt-field">
                <span>Üretilen ürün</span>
                <select value={recipeModal.form.parentStockId} disabled={!!recipeModal.editing} onChange={(e) => setRecipeModal({ ...recipeModal, form: { ...recipeModal.form, parentStockId: e.target.value, childStockId: "" } })} required>
                  <option value="">Seçin…</option>
                  <optgroup label="Mamüller">{byType[3].map((s) => <option key={s.id} value={s.id}>{s.productCode} — {s.productName}</option>)}</optgroup>
                  <optgroup label="Yarı mamüller">{byType[2].map((s) => <option key={s.id} value={s.id}>{s.productCode} — {s.productName}</option>)}</optgroup>
                </select>
              </label>
              <label className="pt-field">
                <span>Kullanılan malzeme</span>
                <select value={recipeModal.form.childStockId} disabled={!recipeModal.form.parentStockId || !!recipeModal.editing} onChange={(e) => setRecipeModal({ ...recipeModal, form: { ...recipeModal.form, childStockId: e.target.value } })} required>
                  <option value="">{recipeModal.form.parentStockId ? "Seçin…" : "Önce üretilen ürünü seçin"}</option>
                  {childOptions(recipeModal.form.parentStockId).map((s) => <option key={s.id} value={s.id}>[{TYPE_LABEL[typeOf(s)]}] {s.productCode} — {s.productName} · {formatNumber(s.currentStock)} {s.unit}</option>)}
                </select>
                <small>Mamül için yarı mamül veya hammadde; yarı mamül için yalnız hammadde seçilebilir.</small>
              </label>
              <div className="pt-grid2">
                <label className="pt-field">
                  <span>Miktar <em>1 {stockById.get(recipeModal.form.parentStockId)?.unit || "birim"} ürün için · {stockById.get(recipeModal.form.childStockId)?.unit || ""}</em></span>
                  <input type="number" inputMode="decimal" step="0.001" min="0.001" required value={recipeModal.form.requiredQuantity} onChange={(e) => setRecipeModal({ ...recipeModal, form: { ...recipeModal.form, requiredQuantity: e.target.value === "" ? "" : Number(e.target.value) } })} />
                </label>
                <label className="pt-field">
                  <span>Fire <em>%</em></span>
                  <input type="number" inputMode="decimal" step="0.1" min="0" max="100" value={recipeModal.form.wastagePercentage} onChange={(e) => setRecipeModal({ ...recipeModal, form: { ...recipeModal.form, wastagePercentage: e.target.value === "" ? "" : Number(e.target.value) } })} />
                </label>
              </div>
              <div className="pt-grid2">
                <label className="pt-field">
                  <span>Açıklama</span>
                  <input value={recipeModal.form.description} onChange={(e) => setRecipeModal({ ...recipeModal, form: { ...recipeModal.form, description: e.target.value } })} placeholder="Örn. 1 gömlek için 2,5 m kumaş" />
                </label>
                <label className="pt-field">
                  <span>Sıra</span>
                  <input type="number" min="1" value={recipeModal.form.sortOrder} onChange={(e) => setRecipeModal({ ...recipeModal, form: { ...recipeModal.form, sortOrder: e.target.value === "" ? "" : Number(e.target.value) } })} />
                </label>
              </div>
            </div>
            <div className="pt-modal-foot">
              <button type="button" className="pt-btn" onClick={() => setRecipeModal(null)}>Vazgeç</button>
              <button type="submit" className="pt-btn primary" disabled={busy}>{busy ? "Kaydediliyor…" : recipeModal.editing ? "Güncelle" : "Ekle"}</button>
            </div>
          </form>
        </div>
      )}

      {/* ===== Üretim modalı ===== */}
      {prodModal && selected && (
        <div className="pt-overlay" onClick={() => setProdModal(null)}>
          <form className="pt-modal wide" onClick={(e) => e.stopPropagation()} onSubmit={runProduction}>
            <div className="pt-modal-head">
              <div><span className="pt-kicker">Üretim</span><h3>{selected.productName}</h3></div>
              <button type="button" className="pt-ib" onClick={() => setProdModal(null)} aria-label="Kapat">{I.close}</button>
            </div>
            <div className="pt-modal-body">
              <div className="pt-grid2">
                <label className="pt-field">
                  <span>Üretim miktarı <em>{selected.unit}</em></span>
                  <input type="number" inputMode="numeric" min="1" step="1" required autoFocus value={prodModal.quantity} onChange={(e) => setProdModal({ ...prodModal, quantity: e.target.value === "" ? "" : Number(e.target.value) })} />
                </label>
                <label className="pt-field">
                  <span>Referans no</span>
                  <input value={prodModal.referenceNo} onChange={(e) => setProdModal({ ...prodModal, referenceNo: e.target.value })} />
                </label>
                <label className="pt-field">
                  <span>Üretimi yapan</span>
                  <input value={prodModal.movedBy} onChange={(e) => setProdModal({ ...prodModal, movedBy: e.target.value })} placeholder="Personel adı" />
                </label>
                <label className="pt-field">
                  <span>İlgili sipariş <em>isteğe bağlı</em></span>
                  <input value={prodModal.relatedOrderId} onChange={(e) => setProdModal({ ...prodModal, relatedOrderId: e.target.value })} placeholder="Sipariş kimliği" />
                </label>
              </div>

              <div className="pt-needs">
                <div className="pt-needs-head">
                  <span>Reçeteye göre düşülecek malzeme</span>
                  {shortages.length > 0
                    ? <span className="pt-needs-bad">{I.warn}{shortages.length} malzemede stok yetersiz</span>
                    : <span className="pt-needs-ok">Stok yeterli</span>}
                </div>
                <table className="pt-table">
                  <thead><tr><th>Malzeme</th><th className="num">Gerekli</th><th className="num">Mevcut</th><th className="num">Kalan</th></tr></thead>
                  <tbody>
                    {productionNeeds.map(({ r, s, need, have, short }) => (
                      <tr key={r.id} className={short > 0 ? "short" : ""}>
                        <td><span className="code">{s?.productCode}</span> {s?.productName}{!!r.wastagePercentage && <em className="fire"> +%{formatNumber(r.wastagePercentage)} fire</em>}</td>
                        <td className="num">{formatNumber(need)} {s?.unit}</td>
                        <td className="num">{formatNumber(have)} {s?.unit}</td>
                        <td className="num">{short > 0 ? <b className="neg">−{formatNumber(short)}</b> : formatNumber(have - need)} {s?.unit}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {shortages.length > 0 && <p className="pt-needs-note">Eksik malzeme varken üretim sunucu tarafından reddedilir. Önce mal kabul yapın ya da miktarı düşürün.</p>}
              </div>
            </div>
            <div className="pt-modal-foot">
              <button type="button" className="pt-btn" onClick={() => setProdModal(null)}>Vazgeç</button>
              <button type="submit" className="pt-btn primary" disabled={busy || shortages.length > 0}>{I.factory}{busy ? "Üretiliyor…" : "Üretimi başlat"}</button>
            </div>
          </form>
        </div>
      )}

      <AlertModal isOpen={alert.isOpen} message={alert.message} type={alert.type} onClose={() => setAlert((a) => ({ ...a, isOpen: false }))} />
      <ConfirmModal isOpen={confirm.isOpen} message={confirm.message} type="danger" onConfirm={confirm.onConfirm} onCancel={() => setConfirm((c) => ({ ...c, isOpen: false }))} />
    </div>
  );
};

export default ProductTree;
